// Read-only rendering of a generated Form 1095-C record. Every value comes
// from the ACA engine's form record (aca_1095c_records); nothing is derived or
// editable here. Corrections go through audited overrides in the Forms tab.
import type React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Help, TermLabel, Explained, CodeHelp } from "@/pages/employer/aca/shared/Help";
import { maskSSNWithX } from "@/lib/utils";

export interface Form1095CRecord {
  id?: number;
  employeeId: number;
  year: number;
  version: number;
  isCorrection?: boolean | null;
  requiredReason?: string | null;
  planStartMonth?: string | null;
  line14?: Record<string, string> | null;
  line15?: Record<string, number | string | null> | null;
  line16?: Record<string, string> | null;
  part3?: Array<{
    dependentId?: number | null;
    firstName: string;
    lastName: string;
    ssn?: string | null;
    dateOfBirth?: string | null;
    months: boolean[];
    allYear: boolean;
  }> | null;
  status?: string | null;
  generatedAt?: string | null;
}

interface EmployeeLike {
  id: number;
  firstName: string;
  lastName: string;
  middleName?: string | null;
  ssn?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  dateOfBirth?: string | null;
}

interface ClientLike {
  id: number;
  name: string;
  ein?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  contactPhone?: string | null;
  phone?: string | null;
}

interface Form1095CProps {
  record: Form1095CRecord;
  employee: EmployeeLike;
  client: ClientLike;
  taxYear: number;
  codes14?: Record<string, string>;
  codes16?: Record<string, string>;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// XXX-XX-1234; also handles already-masked API values ("***-**-1234").
const maskSsn = maskSSNWithX;
const money = (v: number | string | null | undefined) => (v == null || v === "" ? "" : `$${Number(v).toFixed(2)}`);

function Field({ label, value, className = "", help }: { label: string; value?: string | null; className?: string; help?: React.ReactNode }) {
  return (
    <div className={`border rounded p-2 ${className}`}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1">{label}{help}</div>
      <div className="text-sm font-medium min-h-[1.25rem]">{value || <span className="text-muted-foreground">—</span>}</div>
    </div>
  );
}

function ageOnJan1(dob: string | null | undefined, taxYear: number): string {
  if (!dob) return "";
  const d = new Date(dob);
  if (isNaN(d.getTime())) return "";
  const on = new Date(taxYear, 0, 1);
  let age = on.getFullYear() - d.getFullYear();
  const m = on.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && on.getDate() < d.getDate())) age--;
  return String(age);
}

export function Form1095C({ record, employee, client, taxYear, codes14 = {}, codes16 = {} }: Form1095CProps) {
  const l14 = record.line14 || {};
  const l15 = record.line15 || {};
  const l16 = record.line16 || {};
  const c14 = MONTHS.map((_, i) => l14[String(i + 1)] || "");
  const c16 = MONTHS.map((_, i) => l16[String(i + 1)] || "");
  const a15 = MONTHS.map((_, i) => l15[String(i + 1)]);
  const same = (arr: any[]) => arr.every((v) => (v ?? "") === (arr[0] ?? ""));
  const all14 = same(c14) ? c14[0] : "";
  const all15 = same(a15) ? money(a15[0]) : "";
  const all16 = same(c16) ? c16[0] : "";
  const part3 = record.part3 || [];
  const used14 = Array.from(new Set(c14.filter(Boolean)));
  const used16 = Array.from(new Set(c16.filter(Boolean)));

  return (
    <Card className="border-2 border-black print:border">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-xl">Form 1095-C</CardTitle>
            <p className="text-xs text-muted-foreground">Employer-Provided Health Insurance Offer and Coverage · Tax year {taxYear}</p>
          </div>
          <div className="flex items-center gap-2 text-right">
            <Explained term="snapshotVersion"><Badge variant="outline">v{record.version}</Badge></Explained>
            {record.isCorrection && <Explained term="snapshotVersion"><Badge variant="destructive">CORRECTED</Badge></Explained>}
            {record.requiredReason && (
              <Explained term="formRequired">
                <Badge variant="secondary">
                  {record.requiredReason === "self_insured_enrolled" ? "Self-insured enrollee" : "Full-time"}
                </Badge>
              </Explained>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <section>
          <div className="bg-muted px-2 py-1 text-xs font-semibold"><TermLabel term="form1095c">Part I — Employee</TermLabel></div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-2">
            <Field className="md:col-span-3" label="1 Name of employee" value={`${employee.firstName} ${employee.middleName ?? ""} ${employee.lastName}`.replace(/\s+/g, " ")} />
            <Field className="md:col-span-3" label="2 Social security number" value={maskSsn(employee.ssn)} help={<Help term="ssnMasked" />} />
            <Field className="md:col-span-3" label="3 Street address" value={[employee.address1, employee.address2].filter(Boolean).join(", ")} />
            <Field label="4 City or town" value={employee.city} />
            <Field label="5 State" value={employee.state} />
            <Field label="6 ZIP" value={employee.zipCode} />
          </div>
          <div className="bg-muted px-2 py-1 text-xs font-semibold mt-3"><TermLabel term="ale">Applicable Large Employer Member (Employer)</TermLabel></div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-2">
            <Field className="md:col-span-3" label="7 Name of employer" value={client.name} />
            <Field className="md:col-span-3" label="8 Employer identification number (EIN)" value={client.ein} />
            <Field className="md:col-span-3" label="9 Street address" value={[client.address1, client.address2].filter(Boolean).join(", ")} />
            <Field className="md:col-span-3" label="10 Contact telephone number" value={client.contactPhone ?? client.phone} />
            <Field className="md:col-span-2" label="11 City or town" value={client.city} />
            <Field className="md:col-span-2" label="12 State" value={client.state} />
            <Field className="md:col-span-2" label="13 ZIP" value={client.zipCode} />
          </div>
        </section>

        <section>
          <div className="bg-muted px-2 py-1 text-xs font-semibold"><TermLabel term="line14">Part II — Employee Offer of Coverage</TermLabel></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
            <Field label="Employee's age on January 1" value={ageOnJan1(employee.dateOfBirth, taxYear)} help={<Help term="ageOnJan1" />} />
            <Field label="Plan start month" value={record.planStartMonth || "00"} help={<Help term="planStartMonth" />} />
          </div>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-xs border">
              <thead>
                <tr className="bg-muted">
                  <th className="text-left p-1 w-56"></th>
                  <th className="p-1"><TermLabel term="all12Months">All 12 months</TermLabel></th>
                  {MONTHS.map((m) => <th key={m} className="p-1">{m}</th>)}
                </tr>
              </thead>
              <tbody>
                <tr className="border-t">
                  <td className="p-1"><TermLabel term="line14">14 Offer of Coverage (code)</TermLabel></td>
                  <td className="p-1 text-center font-mono font-semibold">{all14 ? <CodeHelp code={all14} line={14} /> : ""}</td>
                  {c14.map((v, i) => <td key={i} className="p-1 text-center font-mono">{all14 ? "" : v ? <CodeHelp code={v} line={14} /> : ""}</td>)}
                </tr>
                <tr className="border-t">
                  <td className="p-1"><TermLabel term="line15">15 Employee Required Contribution</TermLabel></td>
                  <td className="p-1 text-center font-mono font-semibold">{all15}</td>
                  {a15.map((v, i) => <td key={i} className="p-1 text-center font-mono">{all15 ? "" : money(v)}</td>)}
                </tr>
                <tr className="border-t">
                  <td className="p-1"><TermLabel term="line16">16 Section 4980H Safe Harbor and Other Relief (code)</TermLabel></td>
                  <td className="p-1 text-center font-mono font-semibold">{all16 ? <CodeHelp code={all16} line={16} /> : ""}</td>
                  {c16.map((v, i) => <td key={i} className="p-1 text-center font-mono">{all16 ? "" : <CodeHelp code={v || null} line={16}>{v || <span className="text-muted-foreground">·</span>}</CodeHelp>}</td>)}
                </tr>
              </tbody>
            </table>
          </div>
          {(used14.length > 0 || used16.length > 0) && (
            <ul className="mt-2 space-y-0.5 text-[11px] text-muted-foreground">
              {used14.map((c) => <li key={c}><span className="font-mono font-semibold text-foreground">{c}</span> {codes14[c] || ""}</li>)}
              {used16.map((c) => <li key={c}><span className="font-mono font-semibold text-foreground">{c}</span> {codes16[c] || ""}</li>)}
            </ul>
          )}
        </section>

        <section>
          <div className="bg-muted px-2 py-1 text-xs font-semibold">
            <TermLabel term="partIII">Part III — Covered Individuals {part3.length ? "(self-insured coverage)" : "(not applicable — fully insured)"}</TermLabel>
            {" "}<Help term={part3.length ? "selfInsured" : "fullyInsured"} />
          </div>
          {part3.length > 0 && (
            <div className="overflow-x-auto mt-2">
              <table className="w-full text-xs border">
                <thead>
                  <tr className="bg-muted">
                    <th className="text-left p-1">Name</th>
                    <th className="p-1">SSN</th>
                    <th className="p-1">DOB</th>
                    <th className="p-1">All 12</th>
                    {MONTHS.map((m) => <th key={m} className="p-1">{m}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {part3.map((ci, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="p-1">{ci.firstName} {ci.lastName}{ci.dependentId == null && <span className="text-muted-foreground"> (employee)</span>}</td>
                      <td className="p-1 text-center font-mono">{maskSsn(ci.ssn)}</td>
                      <td className="p-1 text-center">{ci.ssn ? "" : (ci.dateOfBirth || "")}</td>
                      <td className="p-1 text-center">{ci.allYear ? "X" : ""}</td>
                      {ci.months.map((m, i) => <td key={i} className="p-1 text-center">{ci.allYear ? "" : m ? "X" : ""}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </CardContent>
    </Card>
  );
}
